import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LayoutDashboard, Users, UserCog, Landmark, Package, Newspaper, HelpCircle, MessageSquareQuote, PhoneCall, Settings as SettingsIcon, LogOut, Landmark as Logo, Sheet } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["super_admin", "admin", "manager", "sales_executive"] },
  { to: "/admin/leads", label: "Leads", icon: Users, roles: ["super_admin", "admin", "manager", "sales_executive"] },
  { to: "/admin/callbacks", label: "Callbacks", icon: PhoneCall, roles: ["super_admin", "admin", "manager", "sales_executive"] },
  { to: "/admin/employees", label: "Employees", icon: UserCog, roles: ["super_admin", "admin", "manager", "sales_executive", "content_manager"] },
  { to: "/admin/lenders", label: "Lenders", icon: Landmark, roles: ["super_admin", "admin", "content_manager"] },
  { to: "/admin/products", label: "Loan Products", icon: Package, roles: ["super_admin", "admin", "content_manager"] },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, roles: ["super_admin", "admin", "content_manager"] },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, roles: ["super_admin", "admin", "content_manager"] },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, roles: ["super_admin", "admin", "content_manager"] },
  { to: "/admin/sheets", label: "Google Sheets", icon: Sheet, roles: ["super_admin", "admin", "manager"] },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin", "admin"] },
];

const ROLE_LABELS = {
  super_admin: "Super Admin", admin: "Admin", manager: "Manager",
  sales_executive: "Sales Executive", content_manager: "Content Manager",
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { document.title = "Admin CRM | BharatBest Fintech"; }, []);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]"><span className="text-sm text-brand-muted">Loading…</span></div>;
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.role === "content_manager" && location.pathname === "/admin") {
    return <Navigate to="/admin/products" replace />;
  }
  const allowedPaths = MENU.filter((m) => m.roles.includes(user.role)).map((m) => m.to);
  const pathAllowed = allowedPaths.some((p) => (p === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(p)));
  if (!pathAllowed) {
    return <Navigate to={allowedPaths[0] || "/admin/login"} replace />;
  }

  const items = MENU.filter((m) => m.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#F5F4F0] flex" data-testid="admin-layout">
      <aside className="w-60 shrink-0 bg-white border-r border-black/5 hidden md:flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-black/5 flex items-center gap-2.5">
          <img src="/assets/bharatbest-logo.png" alt="BharatBest Fintech" className="h-9 w-auto" />
          <div>
            <p className="text-[10px] font-bold tracking-[0.15em] text-brand-gold uppercase">DSA CRM</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" aria-label="Admin">
          {items.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.end}
              data-testid={`admin-nav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[13px] font-semibold transition-colors duration-150 ${isActive ? "bg-brand-red text-white" : "text-brand-dark/60 hover:bg-black/[0.04] hover:text-brand-dark"}`}>
              <m.icon size={16} /> {m.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-black/5">
          <p className="text-sm font-bold text-brand-dark truncate" data-testid="admin-user-name">{user.name}</p>
          <p className="text-xs text-brand-muted">{ROLE_LABELS[user.role] || user.role}</p>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
            className="mt-3 flex items-center gap-2 text-xs font-semibold text-brand-red hover:opacity-80">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden bg-white border-b border-black/5 p-3 flex gap-2 overflow-x-auto sticky top-0 z-40">
          {items.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-bold ${isActive ? "bg-brand-red text-white" : "bg-black/[0.04] text-brand-dark/70"}`}>
              {m.label}
            </NavLink>
          ))}
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-bold bg-black/[0.04] text-brand-red">Sign out</button>
        </div>
        <main className="p-5 md:p-8 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
